import type { Endpoint, CreateEndpointRequest } from '@mockd/shared';
import type { Rule, RuleExport } from '@mockd/shared/types/rule';
import { findEffectiveMethodCondition } from './conditions';

/**
 * The methods an endpoint answers, derived from its rules' conditions.
 * Only conditions that provably constrain the method count — a method leaf
 * inside an OR branch or negated group is ignored. Defaults to GET.
 */
export function endpointMethods(rules: Rule[]): string[] {
  const methods = new Set<string>();
  for (const rule of rules) {
    const method = findEffectiveMethodCondition(rule.conditions)?.value?.toUpperCase();
    if (method) methods.add(method);
  }
  return methods.size > 0 ? [...methods] : ['GET'];
}

/**
 * Generate a cURL command for an endpoint.
 */
export function endpointToCurl(
  baseUrl: string,
  endpoint: Endpoint,
  method: string = 'GET'
): string {
  const url = `${baseUrl}${endpoint.path}`;
  const parts = ['curl'];

  if (method !== 'GET') {
    parts.push('-X', method);
  }

  parts.push(`'${url}'`);

  return parts.join(' ');
}

/**
 * Export format for a single endpoint with its rules.
 */
export interface EndpointExport {
  path: string;
  // Optional because exports written before #262 didn't include them.
  contentType?: string;
  statusCode: number;
  responseBody: string;
  responseHeaders?: Record<string, string>;
  delay: number;
  delayMax?: number;
  rateLimit: number;
  rules?: RuleExport[];
}

export const EXPORT_VERSION = '1.0';

/**
 * Export format for a full project.
 */
export interface ProjectExport {
  version: typeof EXPORT_VERSION;
  exportedAt: string;
  projectName: string;
  endpoints: EndpointExport[];
}

/**
 * Convert endpoints (with optional rules) to a JSON export format.
 */
export function endpointsToJson(
  projectName: string,
  endpoints: Endpoint[],
  rulesMap?: Map<string, Rule[]>
): ProjectExport {
  return {
    version: EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    projectName,
    endpoints: endpoints.map(ep => {
      const exp: EndpointExport = {
        path: ep.path,
        contentType: ep.contentType,
        statusCode: ep.statusCode,
        responseBody: ep.responseBody,
        delay: ep.delay,
        delayMax: ep.delayMax,
        rateLimit: ep.rateLimit,
      };

      if (ep.responseHeaders && Object.keys(ep.responseHeaders).length > 0) {
        exp.responseHeaders = ep.responseHeaders;
      }

      const rules = rulesMap?.get(ep.id);
      if (rules && rules.length > 0) {
        exp.rules = rules.map(r => ({
          name: r.name,
          description: r.description,
          priority: r.priority,
          isActive: r.isActive,
          conditions: r.conditions,
          response: r.response,
          actions: r.actions,
          capturesResponse: r.capturesResponse,
          terminal: r.terminal,
        }));
      }

      return exp;
    }),
  };
}

/**
 * Convert endpoints to a minimal OpenAPI 3.0 spec.
 */
export function endpointsToOpenApi(
  projectName: string,
  baseUrl: string,
  endpoints: Endpoint[],
  rulesMap?: Map<string, Rule[]>
): object {
  const paths: Record<string, Record<string, object>> = {};

  for (const ep of endpoints) {
    // Convert mockd path params (/:param) to OpenAPI style (/{param})
    const openApiPath = ep.path.replace(/:(\w+)/g, '{$1}');

    const rules = rulesMap?.get(ep.id) || [];
    const methods = endpointMethods(rules).map(m => m.toLowerCase());
    // First active rule per method supplies the example response
    const methodRule = new Map<string, Rule>();
    for (const rule of rules) {
      const method = findEffectiveMethodCondition(rule.conditions)?.value?.toLowerCase();
      if (method && rule.isActive && !methodRule.has(method)) {
        methodRule.set(method, rule);
      }
    }

    const pathItem: Record<string, object> = {};

    for (const method of methods) {
      let responseBody: string;
      let statusCode: number;

      const matchingRule = methodRule.get(method);
      if (matchingRule?.response?.steps?.[0]) {
        const step = matchingRule.response.steps[0];
        responseBody = step.body;
        statusCode = step.statusCode;
      } else {
        responseBody = ep.responseBody;
        statusCode = ep.statusCode;
      }

      let exampleValue: unknown;
      try {
        exampleValue = JSON.parse(responseBody);
      } catch {
        exampleValue = responseBody;
      }

      // Merge endpoint-level and rule-level response headers
      const rawHeaders: Record<string, string> = {
        ...(ep.responseHeaders || {}),
        ...(matchingRule?.response?.steps?.[0]?.headers || {}),
      };

      const responseObj: Record<string, unknown> = {
        description: 'Mock response',
        content: {
          'application/json': {
            example: exampleValue,
          },
        },
      };

      if (Object.keys(rawHeaders).length > 0) {
        const openApiHeaders: Record<string, object> = {};
        for (const [name, value] of Object.entries(rawHeaders)) {
          openApiHeaders[name] = {
            schema: { type: 'string' },
            example: value,
          };
        }
        responseObj.headers = openApiHeaders;
      }

      const operation: Record<string, unknown> = {
        summary: `${method.toUpperCase()} ${ep.path}`,
        responses: {
          [String(statusCode)]: responseObj,
        },
      };

      // Add path parameters if present
      const paramMatches = ep.path.match(/:(\w+)/g);
      if (paramMatches) {
        operation.parameters = paramMatches.map(p => ({
          name: p.substring(1),
          in: 'path',
          required: true,
          schema: { type: 'string' },
        }));
      }

      pathItem[method] = operation;
    }

    paths[openApiPath] = pathItem;
  }

  return {
    openapi: '3.0.3',
    info: {
      title: projectName,
      version: '1.0.0',
      description: `Mock API generated by mockd`,
    },
    servers: [{ url: baseUrl }],
    paths,
  };
}

/**
 * Parse pasted import JSON: a mockd project export, a bare array of endpoints,
 * or a single endpoint object. Throws with a user-facing message.
 */
export function parseEndpointImport(parsed: unknown): EndpointExport[] {
  let endpoints: EndpointExport[];
  const obj = parsed as Partial<ProjectExport> & { path?: unknown };

  if (Array.isArray(parsed)) {
    endpoints = parsed;
  } else if (obj && Array.isArray(obj.endpoints)) {
    if (obj.version !== undefined && obj.version !== EXPORT_VERSION) {
      throw new Error(`Unsupported export version "${String(obj.version)}" — this version of mockd reads version ${EXPORT_VERSION}`);
    }
    endpoints = obj.endpoints;
  } else if (obj && obj.path) {
    endpoints = [parsed as EndpointExport];
  } else {
    throw new Error('JSON must be an array of endpoints, an object with an "endpoints" array, or a single endpoint object');
  }

  for (const ep of endpoints) {
    if (!ep.path || typeof ep.path !== 'string') {
      throw new Error(`Each endpoint must have a "path" field (found: ${JSON.stringify(ep.path)})`);
    }
    if (!ep.path.startsWith('/')) {
      throw new Error(`Path must start with "/" (found: "${ep.path}")`);
    }
    if (ep.rules !== undefined && !Array.isArray(ep.rules)) {
      throw new Error(`"rules" for ${ep.path} must be an array`);
    }
  }

  return endpoints;
}

/**
 * Parse pasted rule JSON: a rules download (`{ rules }`), the raw export API
 * response (`{ data }`), or a bare array. Throws with a user-facing message.
 */
export function parseRulesImport(parsed: unknown): RuleExport[] {
  const obj = parsed as { rules?: unknown; data?: unknown } | null;
  const rules = Array.isArray(parsed) ? parsed
    : Array.isArray(obj?.rules) ? obj.rules
    : Array.isArray(obj?.data) ? obj.data
    : null;
  if (!rules) {
    throw new Error('JSON must be an array of rules or an object with a "rules" array');
  }
  for (const rule of rules as Array<Partial<RuleExport>>) {
    if (!rule || typeof rule.name !== 'string' || !rule.name) {
      throw new Error('Each rule must have a "name" field');
    }
  }
  return rules as RuleExport[];
}

/**
 * Rules ready to append to an endpoint. Priority is unique per endpoint, so
 * exported priorities would collide with any rules already there; ordering by
 * them and dropping them lets the server append each one after the last.
 */
export function toAppendableRules(rules: RuleExport[]): Array<Omit<RuleExport, 'priority'>> {
  return [...rules]
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
    .map(({ priority: _priority, ...rest }) => rest);
}

/** One endpoint to import, with the rules to attach once it exists. */
export interface EndpointImportItem {
  endpoint: CreateEndpointRequest;
  rules?: RuleExport[];
}

/**
 * The create request for an imported endpoint — every field export writes,
 * except rules, which are imported separately once the endpoint exists.
 */
export function toCreateRequest(ep: EndpointExport): CreateEndpointRequest {
  return {
    path: ep.path,
    contentType: ep.contentType,
    responseBody: ep.responseBody || '{}',
    statusCode: ep.statusCode ?? 200,
    delay: ep.delay ?? 0,
    delayMax: ep.delayMax,
    rateLimit: ep.rateLimit,
    responseHeaders: ep.responseHeaders,
  };
}

/**
 * Pair each successfully created endpoint with the import item it came from.
 * The bulk route creates items in order and reports failures by index, so the
 * created list is the input list minus the failed indices.
 */
export function pairCreatedEndpoints<T>(
  items: T[],
  result: { data: Endpoint[]; errors: Array<{ index: number }> }
): Array<{ item: T; endpoint: Endpoint }> {
  const failed = new Set(result.errors.map(e => e.index));
  const pairs: Array<{ item: T; endpoint: Endpoint }> = [];
  let next = 0;
  items.forEach((item, i) => {
    if (failed.has(i)) return;
    const endpoint = result.data[next++];
    if (endpoint) pairs.push({ item, endpoint });
  });
  return pairs;
}

/**
 * Trigger a file download in the browser.
 */
export function downloadJson(data: object, filename: string): void {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
